import { Injectable } from '@angular/core';
import { RecordingEvent, MarkerWithLabel } from './measured-data-details.types';
import { translateMarkerLabel } from './measured-data-details-labels';

@Injectable({ providedIn: 'root' })
export class MeasuredDataDetailsRegionService {
  private waveSurferRegions: any;
  private events: RecordingEvent[] = [];
  private selectedEvent: RecordingEvent | null = null;

  init(waveSurferRegions: any, events: RecordingEvent[]): void {
    this.waveSurferRegions = waveSurferRegions;
    this.events = events;
  }

  updateEvents(events: RecordingEvent[]): void {
    this.events = events;
  }

  getEvents(): RecordingEvent[] {
    return this.events;
  }

  addRegionWithMouseEvent(params: any, onSelect: (event: RecordingEvent | null) => void): void {
    if (!this.waveSurferRegions) {
      return;
    }
    const newRegion = this.waveSurferRegions.addRegion(params);

    newRegion.on('over', ((regionId) => {
      return () => {
        const eventData = this.events[regionId];
        this.selectedEvent = eventData;
        onSelect(eventData);
      };
    })(newRegion.id));

    newRegion.on('leave', () => {
      this.selectedEvent = null;
      onSelect(null);
    });
  }

  addMarker(start: number, color: string, label?: string, position = 'top'): MarkerWithLabel {
    const markerWithLabel: MarkerWithLabel = { marker: null, label: null };

    if (!this.waveSurferRegions) {
      return markerWithLabel;
    }

    const newMarker = this.waveSurferRegions.addRegion({
      start,
      drag: false,
      resize: false,
    });

    markerWithLabel.marker = newMarker;

    const markerElement = newMarker.element as HTMLElement | null;
    if (!markerElement) {
      return markerWithLabel;
    }
    markerElement.style.backgroundColor = color;
    markerElement.style.position = 'absolute';
    markerElement.style.height = '20px';
    markerElement.style.width = '10px';
    markerElement.style.marginLeft = '-5px';
    markerElement.style.top = position === 'top'
      ? '0px'
      : 'calc(100% - 20px)'; // wave heightに合わせて上下に配置
    markerElement.style.clipPath = position === 'top'
      ? 'polygon(50% 100%, 100% 60%, 100% 0%, 0% 0%, 0% 60%)'
      : 'polygon(50% 0%, 100% 40%, 100% 100%, 0% 100%, 0% 40%)';
    markerElement.style.zIndex = '200';
    markerElement.classList.add('wave-marker-tag');
    markerElement.dataset.waveMarker = 'true';
    if (!label) {
      return markerWithLabel;
    }

    // ラベルは独立した要素として配置し、三角形のマーカー形状はそのままにする
    const labelElement = document.createElement('div');
    labelElement.textContent = translateMarkerLabel(label);
    labelElement.style.position = 'absolute';
    labelElement.style.whiteSpace = 'nowrap';
    labelElement.style.fontSize = '9px';
    labelElement.style.fontWeight = '600';
    labelElement.style.color = '#333';
    labelElement.style.background = 'transparent';
    labelElement.style.border = 'none';
    labelElement.style.padding = '0 4px';
    labelElement.style.pointerEvents = 'none';
    labelElement.style.zIndex = '9999';
    labelElement.classList.add('wave-marker-label');
    labelElement.dataset.waveMarkerLabel = 'true';

    // 親がまだ DOM に載っていないケースがあるので、数フレーム再試行して append する
    const tryAppend = (retry = 4) => {
      const markerParent = markerElement.parentNode as HTMLElement | null;
      if (markerParent) {
        markerParent.appendChild(labelElement);
        requestAnimationFrame(() => {
          // WaveSurferが設定した%ベースのleft/topをそのまま利用して右横に配置
          labelElement.style.left = markerElement.style.left || '0%';
          labelElement.style.top = markerElement.style.top || '0px';
          labelElement.style.marginLeft = '5px';
          labelElement.style.transform = 'translate(0, 0)';
        });
      } else if (retry > 0) {
        requestAnimationFrame(() => tryAppend(retry - 1));
      }
    };
    tryAppend();

    markerWithLabel.label = labelElement;
    return markerWithLabel;
  }

  removeMarker(markerWithLabel: MarkerWithLabel): void {
    markerWithLabel.marker?.remove();
    markerWithLabel.label?.remove();
  }
}
